/* eslint-disable @angular-eslint/prefer-standalone */
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, Subscription, of, forkJoin } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs/operators';
import { ProjectsAPI } from 'src/app/api/projects.api';
import { ProjectsQuery } from 'src/app/store/projects/projects.query';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';

import { Title } from '@angular/platform-browser';
import { OverlayPanel } from 'primeng/overlaypanel';
import { ConnectionCenterAction } from 'src/app/store/connection-center/connection-center.action';
import { ConnectionCenterQuery } from 'src/app/store/connection-center/connection-center.query';
import { ProjectsActions } from 'src/app/store/projects/projects.action';
import { UserAccountAction } from 'src/app/store/userAccount/user-account.action';
import {
  BoxApi,
  ChatChannel,
  ChatNewService,
  FileHistoryModel,
  InteractionsApi,
  ProjectChannels,
  SentMessage,
  VendorAPI,
  VendorFormatService,
  VendorSearchApi,
  VendorSearchModalComponent,
  ShareFileUnregisteredVendorService,
  ChannelsResponse,
  ChatTypeEnum,
  ArchivedChatData,
  TeamMemberModel,
  IdentityApi,
  QuoteIntentExpressModalComponent,
  getInitials,
  PurchaseOrder
} from 'sustainment-component';
import {
  CertificationUpdateRequest,
  IGeoqueryResRow,
  IProjectList,
  Message,
  MessageFile,
  SupplierListModel,
  VendorStatusEnum
} from 'sustainment-component';
import { ConfirmEmailModalComponent } from './supplier-email-confirmation/modal/confirm-email-modal.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PublicOpportunityApi } from 'src/app/api/public-opportunity.api';
import { ProjectChatService } from 'src/app/services/project-chat.service';
import { OpportunityTeamMemberModel } from 'src/app/containers/projects/projects-view/opportunity-team-popover/opportunity-team-popover.component';
import { Menu } from 'primeng/menu';
import { ViewSwitcherService } from 'src/app/services/view-switcher.service';
import { ConnectionStatus } from 'sustainment-component/lib/types/interactions';
import { ProcurementProjectFromPoModalComponent } from 'src/app/containers/projects/projects-page/procurement-project-from-po-modal/procurement-project-from-po-modal.component';

@Component({
  selector: 'app-dashboard-component',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [DialogService],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  private _viewSwitcherService = inject(ViewSwitcherService);

  public currentAppView = this._viewSwitcherService.currentAppView;
  public isAdmin: boolean = false;
  public chats$: Observable<ChannelsResponse>;
  public conversation$: Observable<Conversation>;

  public selectedChannel: {
    name: string;
    id: string;
    recipient: string;
    projectId?: string;
    isRegistered: boolean;
    type: ChatTypeEnum;
    projectName?: string;
  } | null;
  public companiesExpanded: boolean;

  public files$: Observable<FileHistoryModel[]> | null;
  public companyName$: Observable<string>;
  public showFilesModal = false;
  public showDeletedModal = false;
  public loadingConversation = false;
  public showEmptyOpportunities = false;
  public showEmptyProjects = false;
  public showChatContainer = false;
  public canCreateProjects = false;
  public maxProjects = 0;

  public openProjectAndOpportunityChatAccordions = signal(new Set<string>());

  public purchaseOrder = signal<PurchaseOrder | null>(null);
  public isLoadingPurchaseOrder = signal<boolean>(false);

  public projects: IProjectList[];
  public activeProjects: IProjectList[];

  public showFullLoader: boolean;

  @ViewChild('projectlimit', { static: false })
  public projectlimitOP: OverlayPanel;

  public projCreateMenu = viewChild<Menu>('projCreateMenu');

  public sustainmentId: string;

  private _routerEventsSubscription: Subscription;
  public connectionStatus$: Observable<
    (ConnectionStatus & { note: string }) | null
  >;
  public certRequest$: Observable<CertificationUpdateRequest | null>;
  private _certRequests$: Observable<CertificationUpdateRequest[]>;
  public projectInvite: {
    vendorName: string;
    opportunityId: string;
    status: number;
  } | null;
  public opportunityRequest$: Observable<{
    vendorName: string;
    id: number;
    projectId: string;
    vendorSustainmentId: string;
  } | null>;
  public opportunityRequest: {
    vendorName: string;
    id: number;
    projectId: string;
    vendorSustainmentId: string;
  } | null;
  public nda$: Observable<boolean>;
  private _ndas$: Observable<IGeoqueryResRow[]>;

  public teamMembers$: Observable<
    { members: TeamMemberModel[]; nonMembers: TeamMemberModel[] } | undefined
  >;

  public opportunityTeamMembers$: Observable<OpportunityTeamMemberModel[]>;

  public supplierStatus$: Observable<{ label: string; key: string } | null>;
  public signatureRequest: { id: string; isIncoming: boolean } | null;
  public supplierEmail: { email: string; isConfirmed: boolean } | null;
  public declinedRequestToJoin: boolean = false;
  public activeTeam: TeamMemberModel[];

  public userIsCreator: boolean;
  public isTeamMember: boolean;
  public projectId: string;
  public isLoadingTeamMembers: boolean;
  public isLoadingOpportunityTeamMembers: boolean;

  public opportunityId = signal<string | null>(null);

  @HostListener('window:resize', ['$event'])
  private onResize(): void {
    this.isSmallView = window.innerWidth < 500;
  }

  public constructor(
    private chatService: ChatNewService,
    private router: Router,
    private projectsApi: ProjectsAPI,
    private boxApi: BoxApi,
    private dialogService: DialogService,
    private projectsQuery: ProjectsQuery,
    private userAccountQuery: UserAccountQuery,
    private connectionsQuery: ConnectionCenterQuery,
    private connectionsAction: ConnectionCenterAction,
    private interactionsApi: InteractionsApi,
    private _ndaService: VendorSearchApi,
    private _vendorFormatService: VendorFormatService,
    private _vendorApi: VendorAPI,
    private titleService: Title,
    private route: ActivatedRoute,
    private projectActions: ProjectsActions,
    private _userAccountAction: UserAccountAction,
    private _shareFileUnregisteredVendorService: ShareFileUnregisteredVendorService,
    private _identityApi: IdentityApi,
    private _opportunityApi: PublicOpportunityApi,
    private _projectChatService: ProjectChatService
  ) {
    this.sustainmentId = this.userAccountQuery.sustainmentId;
    this.titleService.setTitle('Homepage - Sustainment Portal');
    this._certRequests$ = this._vendorApi
      .getAllCertificationRequestsAsync()
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
    this._ndas$ = this._ndaService
      .getOrganizationNDA(this.userAccountQuery.sustainmentId)
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
  }

  public isSmallView = false;
  public loadChatView = true;

  public isInitialLoad = signal(true);

  public ngOnInit(): void {
    this.isAdmin = this.userAccountQuery.isAdmin;
    this.projectActions.getProjectsData();
    this._routerEventsSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        //We need to startWith so we capture the first navigation, before we subscribe to the events
        startWith(undefined),
        switchMap(() => {
          const value = this.route.firstChild
            ? this.route.firstChild.paramMap
            : this.route.paramMap;
          return value.pipe(first());
        }),
        mergeMap((params) => {
          const alias = params.get('alias');
          const project = params.get('project');
          const opportunityId = params.get('opportunityId');

          if ((project || opportunityId) && this.isInitialLoad()) {
            this.openProjectAndOpportunityChatAccordions().add(
              String(project || opportunityId)
            );
          }

          this.opportunityId.set(null); // clear the old id

          if (opportunityId) {
            this.opportunityId.set(opportunityId); // then set to the new one
          }

          return this.chatService.channels$.pipe(
            first(),
            map((chats) => {
              const chat = this.findChat(project, chats, alias, opportunityId);

              if (!chat.foundChat) {
                const archivedChat = chats.archivedChats.find(
                  (archChat) => archChat.alias === alias
                );

                if (archivedChat) {
                  this.chatResult(archivedChat);
                } else {
                  if (chats.sourcingProjects.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'projects',
                      chats.sourcingProjects[0].name,
                      'notes'
                    ]);
                  else if (chats.procurementProjects.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'projects',
                      chats.procurementProjects[0].name,
                      'notes'
                    ]);
                  else if (chats.sourcingOpportunities.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'opportunities',
                      chats.sourcingOpportunities[0].opportunityId || ''
                    ]);
                  else if (chats.procurementOrders.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'opportunities',
                      chats.procurementOrders[0].opportunityId || ''
                    ]);
                  else if (chats.companies.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'companies',
                      chats.companies[0].alias || ''
                    ]);
                  else if (chats.archivedChats.length > 0)
                    this.router.navigate([
                      '/',
                      'home',
                      'companies',
                      chats.archivedChats[0].alias || ''
                    ]);
                }
              }
              return chat;
            }),
            filter(
              (
                foundChat
              ): foundChat is {
                foundChat: ChatChannel;
                project: string | null;
              } => !!foundChat.foundChat
            )
          );
        })
      )
      .subscribe(({ foundChat, project }) => {
        this.isInitialLoad.set(false);
        this.selectChat(foundChat, project ?? undefined);
      });

    this.loadChats();
    this.connectionsAction.getConnections();
    this.isSmallView = window.innerWidth < 500;
    this.loadChatView = true;

    this.projectsQuery
      .select((x) => x.projects)
      .pipe(filter((projects) => projects !== undefined))
      .subscribe((projects) => {
        if (projects) {
          this.projects = projects;

          this.maxProjects = this.userAccountQuery.max_projects;

          const activeStatus = ['Open', 'Awarded'];
          this.activeProjects = projects.filter((x) =>
            activeStatus.includes(x.status)
          );
          if (this.activeProjects.length >= this.maxProjects)
            this.canCreateProjects = false;
          else this.canCreateProjects = true;
        }
      });

    this.companyName$ = this.userAccountQuery.userAccount$.pipe(
      map((res) => res?.organizations[0].name)
    );
  }

  public ngOnDestroy(): void {
    this.isInitialLoad.set(true);
    this._routerEventsSubscription.unsubscribe();
    this.onEndChat();
  }

  public onBack(): void {
    this.loadChatView = false;
  }

  public newProject(event: Event, type: string): void {
    event?.stopPropagation();

    if (this.canCreateProjects) {
      if (type === 'sourcing') {
        this.router.navigate(['projects', 'sourcing-creation'], {
          queryParams: { isSourcing: true }
        });
      } else {
        const dialogRef = this.dialogService.open(
          ProcurementProjectFromPoModalComponent,
          { header: 'Add a purchase order' }
        );

        dialogRef.onClose.subscribe((navigateToDraftsHandler?: () => void) => {
          navigateToDraftsHandler?.();
        });
      }
    } else {
      //popover
      this.projectlimitOP.toggle(event);
    }
  }

  public newOpportunityOrder(event: Event, type: string): void {
    event?.stopPropagation();

    if (type === 'order') {
      this.router.navigate(['opportunities', 'order-creation']);
    } else {
      this.router.navigate(['opportunities', 'creation']);
    }
  }

  public openConfirmationModal(
    conversation: Conversation,
    isEdit: boolean
  ): void {
    const ref = this.dialogService.open(ConfirmEmailModalComponent, {
      data: {
        supplierEmail: this.supplierEmail?.email,
        vendorSustainmentId: conversation.vendorSustainmentId,
        isEdit
      },
      showHeader: true,
      header: isEdit ? 'Edit supplier email' : 'Confirm supplier email'
    });

    ref.onClose.subscribe(
      (result: { hasConfirmedEmail: boolean; email: string }) => {
        if (result?.hasConfirmedEmail) {
          this.emailConfirmed(conversation, result.email);
        }
      }
    );
  }

  public emailConfirmed(conversation: Conversation, newEmail: string): void {
    conversation.readonly = false;
    this.supplierEmail!.email = newEmail;
    this.supplierEmail!.isConfirmed = true;
  }

  public loadChats(): void {
    this.chats$ = this.chatService.channels$;
  }

  public newChat(event: Event): void {
    event?.stopPropagation();
    const connections$ = this.connectionsQuery.connections$.pipe(first());
    connections$
      .pipe(
        first((x) => !!x),
        map((x) => x),
        mergeMap((con) => {
          const ref = this.dialogService.open(VendorSearchModalComponent, {
            data: {
              vendors: con,
              title: 'Start new conversation',
              subtitle:
                'Select one of your connections from the list below to start a new conversation.',
              showLogo: true,
              inputLabel: 'Company name'
            },
            showHeader: true,
            header: 'Start new conversation',
            styleClass: 'vendor-select-dialog'
          });

          return ref.onClose.pipe(
            first(),
            filter((x) => !!x)
          );
        }),
        mergeMap((v: SupplierListModel) =>
          this.chatService.channels$.pipe(
            first(),
            map((chat) => {
              const foundChat = chat.companies.find(
                (company) =>
                  company.recipientId === v.sustainmentId?.replace(/-/g, '')
              );

              return { foundChat, selectedVendor: v };
            })
          )
        ),
        mergeMap(({ foundChat, selectedVendor }) => {
          if (foundChat) {
            return of(foundChat);
          } else {
            this.loadingConversation = true;
            // If the chat is not found (meaning it is archived), load it in
            return this.chatService
              .getSingleChannel(selectedVendor.sustainmentId)
              .pipe(
                first(),
                finalize(() => (this.loadingConversation = false)),
                map(() => {
                  return this.chatService.channels$.pipe(
                    first(),
                    map((updatedChat) =>
                      updatedChat.companies.find(
                        (company) =>
                          company.recipientId ===
                          selectedVendor.sustainmentId?.replace(/-/g, '')
                      )
                    )
                  );
                }),
                switchMap((newChat$) => newChat$)
              );
          }
        })
      )
      .subscribe((companyChannel) => {
        if (companyChannel) {
          this.router.navigate([
            '/',
            'home',
            'companies',
            companyChannel.alias
          ]);
          return this.selectChat(companyChannel);
        }
      });
  }

  public onHeaderClick(conversation: Conversation): void {
    if (
      conversation.menu?.length &&
      conversation.menu[0].label === 'Go to project' &&
      conversation.menu[0].command
    ) {
      //@ts-expect-error Ana - check this one
      conversation.menu[0].command();
      return;
    }

    this.openProfile(conversation);
  }

  public getProfileUrl(conversation: Conversation): string {
    const identifier =
      conversation.alias ||
      conversation.vendorSustainmentId ||
      conversation.recipientId.substring(0, 8) +
        '-' +
        conversation.vendorSustainmentId.substring(8, 12) +
        '-' +
        conversation.vendorSustainmentId.substring(12, 16) +
        '-' +
        conversation.vendorSustainmentId.substring(16, 20) +
        '-' +
        conversation.vendorSustainmentId.substring(20);
    return `/profile/${identifier}`;
  }

  public openProfile(conversation: Conversation): void {
    const identifier =
      conversation.alias ||
      conversation.vendorSustainmentId ||
      conversation.recipientId.substring(0, 8) +
        '-' +
        conversation.vendorSustainmentId.substring(8, 12) +
        '-' +
        conversation.vendorSustainmentId.substring(12, 16) +
        '-' +
        conversation.vendorSustainmentId.substring(16, 20) +
        '-' +
        conversation.vendorSustainmentId.substring(20);
    this.router.navigateByUrl(`/profile/${identifier}`);
  }

  public chatResult(
    channel: ChatChannel | ArchivedChatData,
    projectId?: string
  ): void {
    if ('opportunityId' in channel) {
      this.router.navigate([
        '/',
        'home',
        'opportunities',
        channel.opportunityId
      ]);
    } else if (projectId) {
      const projectName =
        this.projects?.find((x) => x.externalId === projectId)?.name || '';
      this.router.navigate([
        '/',
        'home',
        'projects',
        projectName,
        channel.alias
      ]);
    } else {
      if ('isUnread' in channel) {
        this.router.navigate(['/', 'home', 'companies', channel.alias]);
      } else {
        this.loadingConversation = true;
        this.chatService.channels$
          .pipe(
            first(),
            finalize(() => (this.loadingConversation = false)),
            map((chat) => {
              const foundChat = chat.companies.find((company) => {
                return (
                  company.recipientId ===
                  channel.vendorSustainmentId?.replace(/-/g, '')
                );
              });

              return { foundChat, selectedVendor: channel };
            }),
            mergeMap(({ foundChat, selectedVendor }) => {
              if (foundChat) {
                return of(foundChat);
              } else {
                // If the chat is not found (meaning it is archived), load it in
                const vendor = selectedVendor as ArchivedChatData;
                return this.chatService
                  .getSingleChannel(vendor.vendorSustainmentId)
                  .pipe(
                    first(),
                    switchMap(() =>
                      this.chatService.channels$.pipe(
                        first(),
                        map((updatedChat) =>
                          updatedChat.companies.find(
                            (company) =>
                              company.recipientId ===
                              vendor.vendorSustainmentId?.replace(/-/g, '')
                          )
                        )
                      )
                    )
                  );
              }
            })
          )
          .subscribe((companyChannel) => {
            if (companyChannel) {
              this.router.navigate([
                '/',
                'home',
                'companies',
                companyChannel.alias
              ]);
              this.selectChat(companyChannel);
            }
          });
      }
    }
  }

  public selectChat(channel: ChatChannel, projectId?: string): void {
    this.showEmptyOpportunities = false;
    this.showEmptyProjects = false;
    this.showChatContainer = true;
    this.opportunityRequest = null;
    this.declinedRequestToJoin = false;
    this.selectedChannel = {
      id: channel.channel,
      name: channel.name,
      recipient: channel.recipientId,
      projectId: projectId,
      isRegistered: channel.isRegistered,
      type: this.channelToType(channel),
      projectName: channel.projectName
    };

    if (channel.recipientId) {
      this.supplierStatus$ = this._ndaService
        .getStatusHistory(channel.recipientId)
        .pipe(
          map((x) => {
            if (!x.length) return null;
            const currentStatus = x[0];

            const vendorStatusToString: { [key: number]: string } = {
              [VendorStatusEnum.Approved]: 'Approved',
              [VendorStatusEnum.Onboarding]: 'Onboarding',
              [VendorStatusEnum.DoNotUse]: 'Do Not Use'
            };

            const statusString = vendorStatusToString[currentStatus.status];

            if (!statusString) return null;

            return {
              label: statusString,
              key: statusString.toLowerCase().replace(/\s+/g, '')
            };
          })
        );
    }

    this.loadingConversation = true;
    this.conversation$ = this.getConversation(channel, projectId);

    if (
      projectId &&
      this.currentAppView() === 'buyer' &&
      !(channel as any).opportunityId
    ) {
      this.loadPurchaseOrder(projectId);
    } else {
      this.purchaseOrder.set(null);
    }

    this.chatService.joinChannel(channel.channel);

    this.projectInvite =
      'opportunityId' in channel &&
      channel.status !== null &&
      channel.creatorSustainmentId !== this.sustainmentId
        ? {
            vendorName: channel.vendorName,
            opportunityId: channel.opportunityId,
            status: channel.status
          }
        : null;

    if (
      'opportunityRequestId' in channel &&
      channel.opportunityRequestId &&
      projectId
    ) {
      this.opportunityRequest$ = of(channel.opportunityRequestId).pipe(
        switchMap((opportunityRequestId) =>
          opportunityRequestId
            ? this._opportunityApi.checkRequestStatus(
                projectId,
                opportunityRequestId
              )
            : of(false)
        ),
        switchMap((isValid) =>
          isValid && channel.opportunityRequestId && projectId
            ? of({
                vendorName: channel.name,
                id: channel.opportunityRequestId,
                projectId,
                vendorSustainmentId: channel.vendorSustainmentId
              })
            : of(null)
        )
      );

      this.opportunityRequest$.subscribe(
        (value) => (this.opportunityRequest = value)
      );
    }

    this.signatureRequest =
      ('signatureRequest' in channel && channel.signatureRequest) || null;

    this.supplierEmail = !channel.isRegistered
      ? {
          email: channel.supplierEmail?.email || '',
          isConfirmed: channel.supplierEmail?.isConfirmed || false
        }
      : null;
    if (!('opportunityId' in channel) && !('hasQuotes' in channel)) {
      this.nda$ = this._ndas$.pipe(
        map((ndas) =>
          ndas.some(
            (nda) =>
              (nda as unknown as { sustainmentId: string }).sustainmentId ===
              channel.vendorSustainmentId
          )
        )
      );
      this.connectionStatus$ = this.interactionsApi
        .getConnectionStatus(channel.vendorSustainmentId)
        .pipe(filter((st): st is ConnectionStatus & { note: string } => true));
      this.certRequest$ = this._certRequests$.pipe(
        map(
          (certRequests) =>
            certRequests.find(
              (y) => y.requestedBySustainmentId === channel.vendorSustainmentId
            ) || null
        )
      );
    } else {
      this.connectionStatus$ = of(null);
      this.certRequest$ = of(null);
    }

    if (channel.channel.endsWith('notes')) {
      if (!('opportunityId' in channel)) this.getProjectTeam(projectId!);
      else this.getOpportunityTeam(channel.opportunityId);
    } else {
      this.teamMembers$ = of(undefined);
    }
  }

  private getOpportunityTeam(opportunityId: string): void {
    this.isLoadingOpportunityTeamMembers = true;
    this.opportunityTeamMembers$ = this._opportunityApi
      .getOpportunityTeam(opportunityId)
      .pipe(
        mergeMap((team) => {
          return this._identityApi
            .getOrganizationUsers(this.userAccountQuery.sustainmentId)
            .pipe(
              map((orgUsers) => {
                const result = orgUsers.map((user) => ({
                  name: `${user.firstName} ${user.lastName}`,
                  userId: user.externalId,
                  isOwner:
                    team.find((e) => e.userId == user.externalId)?.isOwner ??
                    false,
                  initials: getInitials(`${user.firstName} ${user.lastName}`),
                  isCurrentUser:
                    user.externalId === this.userAccountQuery.getUserId(),
                  isOnTeam: !!team.find((e) => e.userId == user.externalId)
                }));

                this.setActiveTeamFromOpportunityTeam(result);
                this.isLoadingOpportunityTeamMembers = false;

                return result;
              })
            );
        })
      );
    this.opportunityTeamMembers$.subscribe();
  }

  private setActiveTeamFromOpportunityTeam(
    opportunityTeam: OpportunityTeamMemberModel[]
  ): void {
    this.activeTeam = opportunityTeam
      .filter((e) => e.isOnTeam)
      .map((user) => {
        return {
          initials: user.initials,
          projectAdmin: false,
          userId: user.userId,
          isCurrentUser: user.isCurrentUser,
          name: user.name
        };
      });
  }

  private loadPurchaseOrder(projectId: string): void {
    this.purchaseOrder.set(null);

    this.isLoadingPurchaseOrder.set(true);

    this.projectsApi.getPurchaseOrder(projectId).subscribe((po) => {
      if (po && po.length > 0) this.purchaseOrder.set(po[0]);
      else this.purchaseOrder.set(null);

      this.isLoadingPurchaseOrder.set(false);
    });
  }

  public addUserToOpportunityTeam(
    conversation: Conversation,
    user: OpportunityTeamMemberModel
  ): void {
    this.isLoadingOpportunityTeamMembers = true;
    this._opportunityApi
      .addUserToOpportunityTeam(conversation.opportunityId!, user.userId)
      .subscribe(() => {
        this.chatService.loadChannelList();

        const opportunityTeamMembers$ = this.opportunityTeamMembers$.pipe(
          map((value) => {
            value.find((e) => e.userId == user.userId)!.isOnTeam = true;
            this.setActiveTeamFromOpportunityTeam(value);
            this.isLoadingOpportunityTeamMembers = false;
            return value;
          })
        );

        this.opportunityTeamMembers$ = opportunityTeamMembers$;

        opportunityTeamMembers$.subscribe();
      });
  }

  public removeUserFromOpportunityTeam(
    conversation: Conversation,
    user: OpportunityTeamMemberModel
  ): void {
    this.isLoadingOpportunityTeamMembers = true;
    this._opportunityApi
      .removeUserFromOpportunityTeam(conversation.opportunityId!, user.userId)
      .subscribe(() => {
        this.isLoadingOpportunityTeamMembers = false;
        this.chatService.loadChannelList();

        const opportunityTeamMembers$ = this.opportunityTeamMembers$.pipe(
          map((value) => {
            value.find((e) => e.userId == user.userId)!.isOnTeam = false;
            this.setActiveTeamFromOpportunityTeam(value);
            this.isLoadingOpportunityTeamMembers = false;
            return value;
          })
        );

        this.opportunityTeamMembers$ = opportunityTeamMembers$;

        opportunityTeamMembers$.subscribe();
      });
  }

  public onUserLeftOpportunityTeam(conversation: Conversation): void {
    this.isLoadingOpportunityTeamMembers = true;

    this._opportunityApi
      .removeUserFromOpportunityTeam(
        conversation.opportunityId!,
        this.userAccountQuery.getUserId()
      )
      .subscribe(() => {
        this.isLoadingOpportunityTeamMembers = false;
        this.chatService.loadChannelList();

        const opportunityTeamMembers$ = this.opportunityTeamMembers$.pipe(
          map((value) => {
            value.find(
              (e) => e.userId == this.userAccountQuery.getUserId()
            )!.isOnTeam = false;
            this.setActiveTeamFromOpportunityTeam(value);
            this.isLoadingOpportunityTeamMembers = false;
            return value;
          })
        );

        this.opportunityTeamMembers$ = opportunityTeamMembers$;

        opportunityTeamMembers$.subscribe();
      });
  }

  private getProjectTeam(projectId: string): void {
    this.projectId = projectId!;
    this.isLoadingTeamMembers = true;
    this.teamMembers$ = this.projectsApi.getProjectTeam(projectId!).pipe(
      mergeMap((team) => {
        this.isTeamMember = team.some(
          (x) => x === this.userAccountQuery.getUserId()
        );

        return this._identityApi
          .getOrganizationUsers(this.userAccountQuery.sustainmentId)
          .pipe(
            map((orgUsers) => {
              const creatorUser = orgUsers.find(
                (x) =>
                  x.externalId ===
                  this.projects.find((p) => p.externalId === projectId)
                    ?.createdBy
              );

              const projectCreator = creatorUser
                ? {
                    userId: creatorUser.externalId,
                    projectAdmin: true,
                    initials: `${creatorUser.firstName
                      .charAt(0)
                      .toUpperCase()}${creatorUser.lastName
                      .charAt(0)
                      .toUpperCase()}`,
                    name: `${creatorUser.firstName} ${creatorUser.lastName}`,
                    isCurrentUser:
                      creatorUser.externalId ===
                      this.userAccountQuery.getUserId()
                  }
                : {
                    userId: this.userAccountQuery.getUserId(),
                    projectAdmin: true,
                    name: this.userAccountQuery.username,
                    initials: this.userAccountQuery.username
                      .split(' ')
                      .map((x) => x[0])
                      .join(''),
                    isCurrentUser: true
                  };

              this.userIsCreator = projectCreator.isCurrentUser;

              const data = orgUsers.reduce<{
                members: TeamMemberModel[];
                nonMembers: TeamMemberModel[];
              }>(
                (r, o) => {
                  const model: TeamMemberModel = {
                    initials: `${o.firstName
                      .charAt(0)
                      .toUpperCase()}${o.lastName.charAt(0).toUpperCase()}`,
                    name: `${o.firstName} ${o.lastName}`,
                    projectAdmin: false,
                    userId: o.externalId,
                    isCurrentUser:
                      o.externalId === this.userAccountQuery.getUserId()
                  };
                  team.includes(o.externalId)
                    ? r.members.push(model)
                    : r.nonMembers.push(model);
                  return r;
                },
                { members: [], nonMembers: [] }
              );
              if (!data.members.some((x) => x.userId === projectCreator.userId))
                data.members.unshift(projectCreator);
              else
                data.members.forEach((x) => {
                  if (x.userId === projectCreator.userId)
                    x.projectAdmin = projectCreator.projectAdmin;
                });

              this.isLoadingTeamMembers = false;

              this.activeTeam = data.members;

              return data;
            })
          );
      })
    );
  }

  public loadNextPage(): void {
    if (!this.selectedChannel) return;
    this.loadingConversation = true;
    this.chatService.fetchNextPage(this.selectedChannel.id).subscribe({
      complete: () => (this.loadingConversation = false),
      error: () => (this.loadingConversation = false)
    });
  }

  public onMessageSent(message: SentMessage, thread?: string): void {
    if (!this.selectedChannel) return;

    const channelId = !thread
      ? this.selectedChannel.id
      : this.selectedChannel.id + thread;

    const emailTo = !this.selectedChannel.isRegistered
      ? this.supplierEmail?.email
      : undefined;

    const projectName =
      this.projects.find((x) => x.externalId == this.selectedChannel?.projectId)
        ?.name ||
      this.selectedChannel.projectName ||
      '';

    if (message.files.length && this.selectedChannel.projectId) {
      const tempFiles = message.files.map<MessageFile>((file) => ({
        name: file.name,
        id: ''
      }));

      const recipientConversation = {
        channelId: channelId,
        recipient: this.selectedChannel.recipient,
        type: this.selectedChannel.type
      };

      const tmpId = this.chatService.addPendingMessage(
        message.text,
        recipientConversation.recipient,
        recipientConversation.channelId,
        tempFiles,
        emailTo
      );

      this.projectsApi
        .getUploadToken(
          this.selectedChannel.projectId,
          recipientConversation.recipient
        )
        .pipe(
          mergeMap((res) => {
            const fileUploadObservables = message.files.map((file) =>
              this.boxApi
                .uploadAndSolveNameConflicts(file, res.folderId, res.token)
                .pipe(catchError((err: HttpErrorResponse) => of(err)))
            );

            return forkJoin(fileUploadObservables);
          })
        )
        .subscribe({
          next: (boxRes) => {
            const files: MessageFile[] = [];

            boxRes.forEach((res, i) => {
              if (res instanceof HttpErrorResponse) {
                files.push({
                  name: message.files[i].name,
                  id: '', // No id needed as failed messages never upload to box and cannot be clicked
                  status: 'error'
                });
              } else {
                files.push({
                  id: res.entries[0].id,
                  name: res.entries[0].name
                });
              }
            });

            this.chatService.sendMessage(
              message.text,
              recipientConversation.recipient,
              recipientConversation.channelId,
              files,
              tmpId,
              projectName,
              this.selectedChannel?.projectId,
              emailTo,
              message.filesRequireLogin,
              recipientConversation.type,
              message.mentions,
              this.router.url.includes('opportunities')
            );
          },
          error: () =>
            this.chatService.setMessageError(
              recipientConversation.channelId,
              tmpId
            )
        });
    } else {
      this.chatService.sendMessage(
        message.text,
        this.selectedChannel.recipient,
        channelId,
        undefined,
        undefined,
        projectName,
        this.selectedChannel?.projectId,
        emailTo,
        message.filesRequireLogin,
        this.selectedChannel.type,
        message.mentions,
        this.router.url.includes('opportunities')
      );
    }
  }

  public onEndChat(): void {
    this.chatService.leaveChannel();
  }

  public downloadChatFile(fileId: string): void {
    if (!this.selectedChannel) return;

    this.projectsApi
      .downloadFile(this.selectedChannel.projectId!, fileId)
      .subscribe((x) =>
        x ? window.open(x.url, '_blank') : (this.showDeletedModal = true)
      );
  }

  public deleteChatFile(fileId: string): void {
    if (!this.selectedChannel) return;
    if (!this.files$) return;
    this.files$ = this.files$.pipe(
      map((files) => files.filter((file) => file.id !== fileId))
    );
    this.projectsApi
      .deleteFile(this.selectedChannel.projectId!, fileId)
      .subscribe();
  }

  public acceptConnection(connectionCode: string): void {
    this.interactionsApi.acceptConnection(connectionCode).subscribe(
      () =>
        (this.connectionStatus$ = of({
          status: 'Connected',
          code: '',
          note: ''
        }))
    );
  }

  public declineConnection(): void {
    this.connectionStatus$ = of({ status: 'None', code: '', note: '' });
  }

  public projectInviteAccepted(type?: 'opportunity'): void {
    if (type === 'opportunity') {
      if (this.opportunityRequest)
        this.moveVendorRequestToJoin(
          this.opportunityRequest.projectId,
          this.opportunityRequest.vendorSustainmentId
        );

      this.opportunityRequest$ = of(null);
      this.opportunityRequest = null;
      this.declinedRequestToJoin = false;
    }

    if (!this.projectInvite) return;

    if (this.projectInvite.status === 0 || !this.projectInvite.status) {
      const ref = this.dialogService.open(QuoteIntentExpressModalComponent, {
        data: { isNoQuote: false },
        showHeader: false
      });

      ref.onClose.subscribe((message) => {
        if (message) {
          this.projectActions
            .acceptProject(this.projectInvite!.opportunityId, message)
            .subscribe(
              () => (this.projectInvite = { ...this.projectInvite!, status: 1 })
            );
        }
      });
    } else if (this.projectInvite.status === 5) {
      this.projectsApi
        .openQuote(this.projectInvite.opportunityId)
        .subscribe(
          () => (this.projectInvite = { ...this.projectInvite!, status: 1 })
        );
    }
  }

  public projectOwnerInviteDeclined(): void {
    location.reload();
  }

  public moveVendorRequestToJoin(
    projectId: string,
    vendorSustainmentId: string
  ): void {
    this.chats$ = this.chats$.pipe(
      map((response: ChannelsResponse) => {
        const project = this.chatService.getProjectChannelsByProjectId(
          response,
          projectId
        );

        if (project) {
          const updatedChannels = project.channels.map((channel) => {
            if (channel.vendorSustainmentId === vendorSustainmentId) {
              return {
                ...channel,
                status: 1,
                opportunityRequestId: null,
                readonly: false
              };
            }
            return channel;
          });

          return {
            ...response,
            sourcingProjects: response.sourcingProjects.map((p) =>
              p.projectId === projectId
                ? { ...p, channels: updatedChannels }
                : p
            ),
            procurementProjects: response.procurementProjects.map((p) =>
              p.projectId === projectId
                ? { ...p, channels: updatedChannels }
                : p
            )
          };
        }

        return response;
      })
    );
  }

  public projectInviteDeclined(): void {
    if (!this.projectInvite) return;

    const ref = this.dialogService.open(QuoteIntentExpressModalComponent, {
      data: { isNoQuote: true },
      showHeader: false
    });

    ref.onClose.subscribe((message) => {
      if (message) {
        this.projectActions
          .leaveProject(this.projectInvite!.opportunityId, message)
          .subscribe(() => {
            this.projectInvite = { ...this.projectInvite!, status: 5 };
          });
      }
    });
  }

  public hasUnreadChat(project: ProjectChannels): boolean {
    return (
      project.teamMessagesChannel.isUnread ||
      project.channels.some((x) => x.isUnread)
    );
  }

  public projectsHaveUnreadChats(projects: ProjectChannels[]): boolean {
    return projects.some((project) => project.channels.some((x) => x.isUnread));
  }

  private getConversation(
    channel: ChatChannel,
    projectId?: string
  ): Observable<Conversation> {
    const logoUrl = this._vendorFormatService.getLogo(
      channel.vendorSustainmentId,
      channel.logo || null
    );

    let project: IProjectList | undefined;
    if (projectId) {
      this.projectsApi.getItarSetting(projectId).subscribe((value) => {
        this._shareFileUnregisteredVendorService.setValue(value);
      });

      project = this.projects?.find((x) => x.externalId === projectId);
    }

    const menu: MenuItem[] = projectId
      ? [
          {
            label: 'Show file history',
            icon: 'pi pi-history',
            command: (): void => {
              this.showFilesModal = true;
              this.files$ = this.projectsApi
                .getFileHistory(projectId, channel.recipientId)
                .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
            }
          }
        ]
      : [];

    if ('opportunityId' in channel) {
      menu.unshift({
        label: 'Go to project',
        icon: 'pi pi-external-link',
        command: () =>
          window.open(
            this.opportunityId()
              ? `/opportunities/quoting/${projectId}`
              : `/projects/${projectId}`,
            '_blank'
          ),
        visible: false
      });
    }

    return this.chatService.getConversationHistoryNew(channel.channel).pipe(
      finalize(() => (this.loadingConversation = false)),
      map((messages) => ({
        messages,
        alias: channel.alias,
        recipientName: channel.name,
        recipientId: channel.recipientId,
        canUpload: channel.canUpload,
        readonly: channel.readonly,
        logoUrl,
        projectName: project?.name || '',
        projectStatus:
          'hasQuotes' in channel &&
          !channel.channel.endsWith('notes') &&
          project?.status === 'Open'
            ? channel.status === 5 || channel.status === 0
              ? channel.status
              : channel.status === 1
                ? channel.hasQuotes
                  ? 1
                  : 2
                : undefined
            : undefined,
        projectId: projectId,
        opportunityId:
          'opportunityId' in channel ? channel.opportunityId : null,
        vendorSustainmentId: channel.vendorSustainmentId,
        isOfficialSustainmentAccount:
          'isOfficialSustainmentAccount' in channel
            ? channel.isOfficialSustainmentAccount
            : false,
        menu,
        isTeamMessages: channel.channel.endsWith('notes'),
        hasSupplierRecord: channel.hasSupplierRecord,
        isSupplierRecordArchived: channel.isSupplierRecordArchived
      })),
      switchMap((conv) => {
        return this.chatService
          .getLastReadTime(
            channel.channel,
            conv.isTeamMessages
              ? this.userAccountQuery.getUserId().replace(/-/g, '')
              : undefined
          )
          .pipe(
            map((lastReadTime) => ({
              ...conv,
              lastReadTime: new Date(+lastReadTime / 10000)
            }))
          );
      })
    );
  }

  private channelToType(channel: ChatChannel): ChatTypeEnum {
    if ('opportunityId' in channel) {
      return channel.channel.endsWith('notes')
        ? ChatTypeEnum.Notes
        : ChatTypeEnum.Opportunity;
    }
    if ('opportunityRequestId' in channel)
      return channel.channel.endsWith('notes')
        ? ChatTypeEnum.Notes
        : ChatTypeEnum.Project;
    return ChatTypeEnum.Company;
  }

  private findChat(
    project: string | null,
    chats: ChannelsResponse,
    alias: string | null,
    opportunityId: string | null
  ): { foundChat: ChatChannel | undefined; project: string | null } {
    let foundChat: ChatChannel | undefined;
    if (project) {
      const projectChannels = this.chatService.getProjectChannelsByProjectId(
        chats,
        project
      );

      if (!projectChannels) return { foundChat: undefined, project: null };

      project = projectChannels.projectId;

      if (alias === 'notes') {
        foundChat = {
          channel: projectChannels.teamMessagesChannel.channel,
          alias: 'notes',
          canUpload: false,
          hasQuotes: false,
          isOfficialSustainmentAccount: false,
          isRegistered: true,
          isUnread: false,
          lastMessageTimeStamp: 0,
          logo: null,
          name: 'Team Messages',
          opportunityRequestId: null,
          vendorName: 'Team Messages',
          status: 0,
          recipientId: '',
          readonly: false,
          supplierEmail: null,
          vendorSustainmentId: '',
          hasSupplierRecord: false,
          isSupplierRecordArchived: false,
          projectName: projectChannels.name
        };
      } else {
        foundChat = projectChannels.channels.find((ch) => ch.alias === alias);
      }
    } else if (alias) {
      foundChat = chats.companies.find((company) => company.alias === alias);
    } else if (opportunityId) {
      foundChat = this.chatService.getOpportunityChannelByOpportunityId(
        chats,
        opportunityId
      );

      if (this.router.url.endsWith('notes')) {
        foundChat = {
          channel: foundChat!.teamMessagesChannel.channel,
          alias: 'notes',
          canUpload: false,
          hasQuotes: false,
          isOfficialSustainmentAccount: false,
          isRegistered: true,
          isUnread: false,
          lastMessageTimeStamp: 0,
          logo: null,
          name: 'Team Messages',
          opportunityRequestId: null,
          vendorName: 'Team Messages',
          status: 0,
          recipientId: '',
          readonly: false,
          supplierEmail: null,
          vendorSustainmentId: '',
          hasSupplierRecord: false,
          isSupplierRecordArchived: false,
          opportunityId: foundChat?.opportunityId,
          projectName: foundChat?.opportunityName
        };
      }
    }

    return { foundChat, project: project || opportunityId };
  }

  /* Project team popover */
  public onUserAddedToTeam(user: TeamMemberModel): void {
    this.projectsApi
      .addTeamMember(this.projectId, user.userId)
      .subscribe(() => {
        if (user.userId === this.userAccountQuery.getUserId()) {
          this.isTeamMember = true;
          this.chatService.loadChannelList();
          this._projectChatService.getTeamNotes();
        }
      });

    this.isLoadingTeamMembers = true;
    this.teamMembers$ = this.teamMembers$.pipe(
      map((x) => {
        if (x) {
          x.members.push(user);

          // remove duplicates
          x.members = x.members.filter(
            (v, i, a) => a.findIndex((t) => t.userId === v.userId) === i
          );

          x.nonMembers = x.nonMembers.filter(
            (v, i, a) => a.findIndex((t) => t.userId === v.userId) === i
          );
        }

        this.isLoadingTeamMembers = false;

        this.activeTeam = x?.members ?? [];
        return x;
      })
    );
  }

  /* Project team popover */
  public onUserRemovedFromTeam(user: TeamMemberModel): void {
    this.projectsApi.deleteTeamMember(this.projectId, user.userId).subscribe();

    this.isLoadingTeamMembers = true;
    this.teamMembers$ = this.teamMembers$.pipe(
      map((x) => {
        if (x) x.members = x.members.filter((m) => m.userId !== user.userId);

        this.isLoadingTeamMembers = false;
        this.activeTeam = x?.members ?? [];
        return x;
      })
    );
  }

  public leaveTeam(): void {
    this.isTeamMember = false;
    this.projectsApi
      .deleteTeamMember(this.projectId, this.userAccountQuery.getUserId())
      .subscribe(() => this.chatService.loadChannelList());
  }

  public updateChatAccordionStates(
    newOpenState: boolean,
    projNameOrOppId: string
  ): void {
    const isProjectChatAccordionAlreadyOpen =
      this.openProjectAndOpportunityChatAccordions().has(projNameOrOppId);

    this.openProjectAndOpportunityChatAccordions.update((prev) => {
      if (!isProjectChatAccordionAlreadyOpen && newOpenState) {
        prev.add(projNameOrOppId);
      } else {
        prev.delete(projNameOrOppId);
      }
      return prev;
    });
  }
}

export interface Conversation {
  messages: Message[];
  recipientName: string;
  recipientId: string;
  canUpload: boolean;
  menu: MenuItem[];
  readonly: boolean;
  projectName: string;
  projectStatus?: number;
  opportunityId: string | null;
  logoUrl: string;
  vendorSustainmentId: string;
  alias: string;
  lastReadTime: Date;
  isOfficialSustainmentAccount: boolean;
  isTeamMessages: boolean;
  hasSupplierRecord: boolean;
  isSupplierRecordArchived: boolean;
  projectId?: string;
}
